import { cn } from '../lib/utils';
import brandMarkDark from '../assets/brand-mark-dark.webp';
import brandMarkLight from '../assets/brand-mark-light.webp';

export interface BrandMarkProps {
  /** The wordmark. Hidden when `markOnly`, but still read by screen readers. */
  name: string;
  /** Small caps line under the name — "Sports agency", "Back office". */
  tagline?: string;
  /** Glyph only — for the collapsed sidebar. */
  markOnly?: boolean;
  size?: 'sm' | 'md';
  /**
   * Which glyph to draw. `auto` follows the theme (dark glyph on the light
   * theme, light glyph on the dark theme) and is right for the sidebar and
   * the sign-in form. Force `light` on a surface that is dark in both themes.
   */
  tone?: 'auto' | 'light' | 'dark';
  className?: string;
}

/**
 * The logo lockup: the sunburst glyph, the name, and an optional tagline.
 *
 * The glyph ships as two files — a dark one and a light one — rather than
 * one recoloured with CSS, because it is a raster and the brand's exact
 * off-white matters. `auto` renders both and lets the `.dark` class on
 * `<html>` pick, so the swap needs no JavaScript and happens before paint.
 */
export function BrandMark({
  name,
  tagline,
  markOnly = false,
  size = 'md',
  tone = 'auto',
  className,
}: BrandMarkProps) {
  const glyphClass = cn('shrink-0 object-contain', size === 'md' ? 'size-12' : 'size-8');

  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      {tone === 'auto' ? (
        <>
          <img src={brandMarkDark} alt="" className={cn(glyphClass, 'dark:hidden')} />
          <img src={brandMarkLight} alt="" className={cn(glyphClass, 'hidden dark:block')} />
        </>
      ) : (
        <img
          src={tone === 'light' ? brandMarkLight : brandMarkDark}
          alt=""
          className={glyphClass}
        />
      )}

      {markOnly ? (
        <span className="sr-only">{name}</span>
      ) : (
        <div className="grid min-w-0 leading-tight">
          <span className="truncate text-base font-semibold text-foreground">{name}</span>
          {tagline ? (
            <span className="truncate text-[10px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
              {tagline}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
